/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { AppInfo, App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { DeviceInfo } from "@capacitor/device";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonSpinner,
  IonToolbar,
  useIonRouter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { Suspense, useCallback, useEffect } from "react";
import { useHistory, withRouter } from "react-router";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { Button_Help_White, Button_Menu } from "@assets/images";
import ContentContainer from "@components/common/ContentContainer";
import CPDOverview from "@components/home/CPDOverview";
import EvidenceOverview from "@components/home/EvidenceOverview";
import DataController from "@controllers/DataController";
import * as FirebaseService from "@controllers/FirebaseService";
import * as ProgramUtils from "@utils/programUtils";
import { AllProgressCheckStatuses, IClassSubscription, IUser, ProgramData } from "@interfaces/Interfaces";
import { appInfoAtom, deviceInfoAtom, progressCheckDataAtom, userAtom } from "@state/State";
import { HeaderTitle } from "parafolio-components";
import ProgramCard from "@components/common/ProgramCard";
import { useDebugUser } from "@hooks/auth/useDebugUser";
import { useProgramData } from "@hooks/data/useProgramData";
import "./Dashboard.css";
import "./News.css";
import WhatsNewCard from "@components/home/WhatsNewCard";
import AppOutdatedBanner from "@components/home/AppOutdatedBanner";

const Dashboard: React.FC = () => {
  const { canGoBack } = useIonRouter();
  const { isDebugUser } = useDebugUser();
  const { userPrograms } = useProgramData();
  const history = useHistory();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const deviceInfo = useRecoilValue<DeviceInfo | null>(deviceInfoAtom);
  const appInfo = useRecoilValue<AppInfo | null>(appInfoAtom);

  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const sendSupportEmail = useCallback((): void => {
    if (user.state === "hasValue" && user.contents) {
      const url = "mailto:apps@class.co.uk";
      const subject = "ParaFolio - General support";

      const body = DataController.supportEmailBody(user.contents.userData, deviceInfo, appInfo);

      window.open(`${url}?subject=${subject}&body=${body}`, "_blank");
    }
  }, [appInfo, deviceInfo, user]);

  const sortedPrograms = getSortedPrograms();

  useEffect(() => {
    const setPageName = async (): Promise<void> => {
      await FirebaseService.setScreenName("dashboard");
    };

    setPageName();
  }, []);

  function getSortedPrograms(): ProgramData[] {
    if (!user) return [];

    const notStartedPrograms: ProgramData[] = [];
    const inProgressPrograms: ProgramData[] = [];
    const completedPrograms: ProgramData[] = [];
    userPrograms.forEach((prog) => {
      const subscriptions = user.contents?.userData.subscriptions || [];
      const programSubscriptionIDs = prog?.Subscription || [];

      const programSubscription: IClassSubscription = subscriptions.find((item: IClassSubscription | any) => {
        const subscriptionPrograms: string[] = item.ParaFolioPrograms;

        return (
          subscriptionPrograms?.some((_sub) => prog?.Subscription?.includes(_sub)) ||
          programSubscriptionIDs.includes(`${item.SubscriptionID}`)
        );
      });

      if (programSubscription) {
        let json: any = {};

        try {
          if (programSubscription.App_Data) {
            json = JSON.parse(programSubscription.App_Data);
          }
        } catch (error) {
          console.log(error);
        }

        if (Object.keys(json).length === 0 || !json[`${prog?.ID}`]?.startDate) {
          notStartedPrograms.push(prog);
        } else {
          if (prog.ProgressChecks?.length) {
            // handle programs with progress checks
            const allProgressChecksDone = prog.ProgressChecks?.every((pc) => {
              const progressCheckStatus = ProgramUtils.getProgressCheckStatusFromEvidence(
                pc.ID,
                allProgressCheckData.contents,
                prog
              );
              return progressCheckStatus?.submissions.status === "Completed";
            });
            if (allProgressChecksDone) {
              completedPrograms.push(prog);
            } else {
              inProgressPrograms.push(prog);
            }
          } else {
            // handle programs without progress checks
            inProgressPrograms.push(prog);
          }
        }
      }
    });

    // sort groups alphabetically
    notStartedPrograms.sort((a, b) => (a.Title ?? "").localeCompare(b.Title ?? ""));
    inProgressPrograms.sort((a, b) => (a.Title ?? "").localeCompare(b.Title ?? ""));
    completedPrograms.sort((a, b) => (a.Title ?? "").localeCompare(b.Title ?? ""));

    const result = [...notStartedPrograms, ...inProgressPrograms, ...completedPrograms];
    return result;
  }

  const handleAppExit = (event: any): void => {
    if (Capacitor.getPlatform() === "android") {
      event.detail!.register(-1, () => {
        if (!canGoBack()) {
          CapacitorApp.exitApp();
        }
      });
    }
  };

  const handleClick = async (event: Event): Promise<void> => {
    const element = event.target as HTMLAnchorElement;

    if (element.href?.includes("mailto:apps@class.co.uk")) {
      event.preventDefault();
      await FirebaseService.logEvent("support_link_pressed", {
        link: "contact_support",
        type: "app_info_feedback",
      });

      sendSupportEmail();
    }
  };

  const handleAuxClick = async (event: MouseEvent): Promise<void> => {
    const element = event.target as HTMLAnchorElement;

    if (event.button === 1) {
      if (element.href?.includes("mailto:apps@class.co.uk")) {
        event.preventDefault();
        await FirebaseService.logEvent("support_link_pressed", {
          link: "contact_support",
          type: "app_info_feedback",
        });

        sendSupportEmail();
      }
    }
  };

  useIonViewWillEnter(() => {
    document.addEventListener("ionBackButton", handleAppExit);
    document.addEventListener("click", handleClick);
    document.addEventListener("auxclick", handleAuxClick);
  });

  useIonViewDidLeave(() => {
    document.removeEventListener("ionBackButton", handleAppExit);
    document.removeEventListener("click", handleClick);
    document.removeEventListener("auxclick", handleAuxClick);
  });

  const helpCentrePressed = async (): Promise<void> => {
    await FirebaseService.logEvent("help_centre_pressed");
    history.push("/dashboard/help-centre");
  };

  return (
    <IonPage>
      <Suspense
        fallback={
          <div className="loadingScreen">
            <IonSpinner className="loadingScreenSpinner" />
            <div className="loadingScreenText">{"Loading"}</div>
          </div>
        }
      >
        <IonHeader>
          <IonToolbar
            mode="ios"
            className="navBar"
            style={{
              maxWidth: DataController.isWeb() ? 980 : undefined,
              height: DataController.getHeaderHeight(),
            }}
          >
            <IonButtons slot="start">
              <IonMenuButton>
                <img
                  alt="Menu"
                  title="Menu"
                  src={Button_Menu}
                  className="menuIcon"
                />
              </IonMenuButton>
            </IonButtons>
            <HeaderTitle>{`ParaFolio${isDebugUser ? " (DEBUG)" : ""}`}</HeaderTitle>
            <IonButtons slot="end">
              <IonButton
                className="header-button-icon-with-text"
                mode="ios"
                onClick={() => helpCentrePressed()}
              >
                <div className="-mr-1 text-17px text-white">{"Help"}</div>
                <img
                  alt="Help"
                  title="Help"
                  src={Button_Help_White}
                  className="headerIcon"
                />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="page-background relative">
          <AppOutdatedBanner />
          <ContentContainer id="dashboardContentContainer">
            <WhatsNewCard />
            {sortedPrograms.map((p) => (
              <div key={p.ID}>
                {user.state === "hasValue" && (
                  <ProgramCard
                    userObject={user.contents}
                    programID={p.ID}
                  />
                )}
              </div>
            ))}
            <EvidenceOverview />
            <CPDOverview />
          </ContentContainer>
        </IonContent>
      </Suspense>
    </IonPage>
  );
};

export default withRouter(Dashboard);
